// File: clientapp/src/pages/Account.js
import React, { useContext, useState } from 'react';
import {
    Container,
    Paper,
    Box,
    Avatar,
    Typography,
    TextField,
    Button,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { AuthContext } from '../contexts/AuthContext';

export default function Account() {
    const { user } = useContext(AuthContext);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');

    // Define password requirements:
    const requirements = [
        { label: 'At least 6 characters', test: (pwd) => pwd.length >= 6 },
        { label: 'At least one digit', test: (pwd) => /\d/.test(pwd) },
        { label: 'At least one uppercase letter', test: (pwd) => /[A-Z]/.test(pwd) },
        { label: 'At least one lowercase letter', test: (pwd) => /[a-z]/.test(pwd) }
    ];

    // Check if each requirement is met based on newPassword
    const isRequirementMet = (req) => req.test(newPassword);
    const allRequirementsMet = requirements.every(req => req.test(newPassword));
    const passwordsMatch = newPassword === confirmPassword && newPassword !== '';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        if (!allRequirementsMet) {
            setMessage('New password does not meet requirements.');
            return;
        }
        if (!passwordsMatch) {
            setMessage('New password and confirm password do not match.');
            return;
        }
        try {
            const response = await fetch('/api/account/changePassword', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ currentPassword, newPassword })
            });
            if (response.ok) {
                setMessage('Password changed successfully.');
                // Optionally clear the fields
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
            } else {
                const errData = await response.json();
                setMessage(errData.message || 'Failed to change password.');
            }
        } catch (error) {
            console.error(error);
            setMessage('Error changing password.');
        }
    };

    const displayName = user?.userName || user?.email || 'User';
    const userInitial = displayName.charAt(0).toUpperCase();

    return (
        <Container>
            <Paper style={{ padding: 16, marginTop: 16 }}>
                <Box display="flex" alignItems="center" mb={2}>
                    <Avatar sx={{ width: 50, height: 50, mr: 2 }}>{userInitial}</Avatar>
                    <Typography variant="h6">{displayName}</Typography>
                </Box>
                <Typography variant="subtitle1" gutterBottom>
                    Change Password
                </Typography>
                <form onSubmit={handleSubmit}>
                    <TextField
                        label="Current Password"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        fullWidth
                        margin="normal"
                        required
                    />
                    <TextField
                        label="New Password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        fullWidth
                        margin="normal"
                        required
                    />
                    <TextField
                        label="Confirm New Password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        fullWidth
                        margin="normal"
                        required
                    />

                    <Box mt={2}>
                        <Typography variant="subtitle2">Password Requirements:</Typography>
                        <List>
                            {requirements.map((req, index) => (
                                <ListItem key={index}>
                                    <ListItemIcon>
                                        {isRequirementMet(req) ? (
                                            <CheckCircleIcon style={{ color: 'green' }} fontSize="small" />
                                        ) : (
                                            <CancelIcon style={{ color: 'red' }} fontSize="small" />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText primary={req.label} />
                                </ListItem>
                            ))}
                        </List>
                        {!passwordsMatch && newPassword && (
                            <Typography variant="body2" style={{ color: 'red' }}>
                                New password and confirm password do not match.
                            </Typography>
                        )}
                    </Box>

                    {message && (
                        <Typography variant="body2" color="primary" style={{ marginTop: 8 }}>
                            {message}
                        </Typography>
                    )}

                    <Button variant="contained" type="submit" fullWidth style={{ marginTop: 16 }}>
                        Change Password
                    </Button>
                </form>
            </Paper>
        </Container>
    );
}
